import { ClerkProvider } from '@clerk/clerk-react';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { type ReactElement, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { buildClerkAppearance } from '@/auth/clerkAppearance';
import { AuthProvider } from '@/auth/useAuth';
import { OrganisationFilterProvider } from '@/features/organisations/providers/OrganisationFilterProvider';
import { ProjectFilterProvider } from '@/features/projects/hooks/ProjectFilterProvider';
import { ThemeProvider } from '@/theme/ThemeProvider';
import { r } from '@/theme/tokens/radii';
import { App } from './App';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { NotificationProvider } from './providers/NotificationProvider';
import { createAppQueryClient } from './shared/data/queryClient';
import { DisplayPreferencesProvider } from './shared/hooks/DisplayPreferencesProvider';
import { useThemeMode } from './theme/ThemeModeContext';

const queryClient = createAppQueryClient();
type AppRootProps = {
  clerkPublishableKey?: string;
  authBypassClerk?: boolean;
};

export const AppRoot = ({
  clerkPublishableKey,
  authBypassClerk = false,
}: AppRootProps): ReactElement => {
  const { mode } = useThemeMode();
  const clerkPrimaryColor = '#008080';
  const passwordToggleColor = mode === 'dark' ? '#f2f2f2' : '#1a1a1a';
  const resendCodeColor = mode === 'dark' ? '#27d3d3' : '#007070';
  const cardRadius = r('card');
  const controlRadius = r('md');
  const clerkAppearance = useMemo(
    () =>
      buildClerkAppearance({
        mode,
        cardRadius,
        controlRadius,
        clerkPrimaryColor,
        passwordToggleColor,
        resendCodeColor,
      }),
    [cardRadius, clerkPrimaryColor, controlRadius, mode, passwordToggleColor, resendCodeColor],
  );

  const appShell = (
    <QueryClientProvider client={queryClient}>
      <NotificationProvider>
        <DisplayPreferencesProvider>
          <AuthProvider>
            <OrganisationFilterProvider>
              <ProjectFilterProvider>
                <AppErrorBoundary>
                  <App />
                </AppErrorBoundary>
              </ProjectFilterProvider>
            </OrganisationFilterProvider>
          </AuthProvider>
        </DisplayPreferencesProvider>
      </NotificationProvider>
    </QueryClientProvider>
  );

  const appContent =
    authBypassClerk || !clerkPublishableKey ? (
      appShell
    ) : (
      <ClerkProvider
        publishableKey={clerkPublishableKey}
        afterSignOutUrl="/sign-in"
        appearance={clerkAppearance}
      >
        {appShell}
      </ClerkProvider>
    );

  return (
    <BrowserRouter>
      <ThemeProvider mode={mode} density="compact">
        {appContent}
      </ThemeProvider>
    </BrowserRouter>
  );
};
